import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapIcon, UserIcon, CubeIcon } from '@heroicons/react/24/outline';
import type { ThumbnailData } from '../utils/thumbnailUtils';
import { limitThumbnails } from '../utils/thumbnailUtils';
import ThumbnailDetailDialog from '../dialogs/ThumbnailDetailDialog';

interface ShotCardThumbnailsProps {
  thumbnails: ThumbnailData[];
}

/**
 * 分镜卡片缩略图区域组件
 * 
 * 显示在卡片左侧，垂直排列场景、角色、道具的缩略图
 */
export default function ShotCardThumbnails({ thumbnails }: ShotCardThumbnailsProps) {
  const { t } = useTranslation(['script', 'common']);
  const [selectedThumbnail, setSelectedThumbnail] = useState<ThumbnailData | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  // 限制缩略图数量最多 5 个
  const { limited, remaining } = limitThumbnails(thumbnails);

  // 如果没有缩略图，不渲染
  if (thumbnails.length === 0) {
    return null;
  }

  // 根据类型获取占位图标
  const getPlaceholderIcon = (type: ThumbnailData['type']) => {
    switch (type) {
      case 'scene':
        return MapIcon;
      case 'character':
        return UserIcon;
      case 'prop':
        return CubeIcon;
    }
  };

  // 处理图片加载失败
  const handleImageError = (index: number) => {
    setImageErrors(prev => new Set(prev).add(index));
    // 记录警告信息
    console.warn(`Failed to load thumbnail image at index ${index}:`, thumbnails[index]?.imageUrl);
  };

  return (
    <>
      {/* 左侧缩略图区域 - 垂直排列 */}
      <div className="flex flex-col gap-2 flex-shrink-0">
        {limited.map((thumbnail, index) => {
          const PlaceholderIcon = getPlaceholderIcon(thumbnail.type);
          const hasImageError = imageErrors.has(index);
          const showPlaceholder = !thumbnail.imageUrl || hasImageError;

          return (
            <div
              key={index}
              className="relative group"
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* 缩略图 - 固定尺寸 */}
              <button
                onClick={() => setSelectedThumbnail(thumbnail)}
                className="w-12 h-12 rounded-md border border-border-subtle overflow-hidden bg-bg-elevated hover:border-border-default transition-colors cursor-pointer"
                title={thumbnail.name}
              >
                {showPlaceholder ? (
                  // 占位符
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <PlaceholderIcon className="w-8 h-8" />
                  </div>
                ) : (
                  // 图片
                  <img
                    src={thumbnail.imageUrl}
                    alt={thumbnail.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(index)}
                  />
                )}
              </button>

              {/* Tooltip - 悬停显示元素名称（在右侧显示） */}
              {hoveredIndex === index && (
                <div className="hidden md:block absolute left-full ml-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                  <div className="bg-bg-page text-gray-50 text-xs px-2 py-1 rounded whitespace-nowrap shadow-lg">
                    {thumbnail.name}
                  </div>
                  {/* 箭头 */}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 -mr-1">
                    <div className="w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-gray-900" />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* "更多"提示 */}
        {remaining > 0 && (
          <div
            className="w-12 h-12 rounded-md bg-bg-elevated flex items-center justify-center border border-border-subtle"
            title={t('thumbnail.moreCount', { count: remaining })}
          >
            <span className="text-xs text-gray-400">+{remaining}</span>
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      <ThumbnailDetailDialog
        isOpen={selectedThumbnail !== null}
        onClose={() => setSelectedThumbnail(null)}
        thumbnail={selectedThumbnail}
      />
    </>
  );
}

